import React, { useState, useEffect } from 'react';
import { ModernNavigation } from '../components/modern/ModernNavigation';
import { ModernFooter } from '../components/modern/ModernFooter';
import '../css/modern-design-system.css';
import '../css/modern-privacy.css';

export function ModernPrivacyPolicy() {
  const [scrolled, setScrolled] = useState(false);
  const [activeSection, setActiveSection] = useState('');

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
      
      // Update active section based on scroll position
      const sections = document.querySelectorAll('.privacy-section');
      const scrollPosition = window.scrollY + 200;
      
      sections.forEach(section => {
        const top = (section as HTMLElement).offsetTop;
        const height = (section as HTMLElement).offsetHeight;
        const id = section.getAttribute('id');
        
        if (scrollPosition >= top && scrollPosition < top + height && id) {
          setActiveSection(id);
        }
      });
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const offset = 100;
      const elementPosition = element.offsetTop - offset;
      window.scrollTo({
        top: elementPosition,
        behavior: 'smooth'
      });
    }
  };

  return (
    <div className="modern-privacy-page">
      <ModernNavigation scrolled={scrolled} />
      
      {/* Hero Section */}
      <section className="privacy-hero">
        <div className="privacy-hero-bg"></div>
        <div className="container">
          <div className="privacy-hero-content">
            <div className="privacy-badge">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
              </svg>
              <span>Your Privacy Matters</span>
            </div>
            <h1 className="privacy-hero-title">Privacy Policy</h1>
            <p className="privacy-hero-subtitle">
              Last Updated: March 18, 2024
            </p>
            <div className="privacy-hero-meta">
              <span>Effective Date: March 18, 2024</span>
              <span>•</span>
              <span>Version 2.0</span>
            </div>
          </div>
        </div>
      </section>

      <div className="privacy-content-wrapper">
        <div className="container">
          <div className="privacy-layout">
            {/* Sidebar Navigation */}
            <aside className="privacy-sidebar">
              <div className="sidebar-sticky">
                <h3 className="sidebar-title">Table of Contents</h3>
                <nav className="privacy-nav">
                  <a 
                    href="#overview"
                    className={`nav-item ${activeSection === 'overview' ? 'active' : ''}`}
                    onClick={(e) => {
                      e.preventDefault();
                      scrollToSection('overview');
                    }}
                  >
                    Overview
                  </a>
                  <a 
                    href="#information-we-collect"
                    className={`nav-item ${activeSection === 'information-we-collect' ? 'active' : ''}`}
                    onClick={(e) => {
                      e.preventDefault();
                      scrollToSection('information-we-collect');
                    }}
                  >
                    Information We Collect
                  </a>
                  <a 
                    href="#how-we-use"
                    className={`nav-item ${activeSection === 'how-we-use' ? 'active' : ''}`}
                    onClick={(e) => {
                      e.preventDefault();
                      scrollToSection('how-we-use');
                    }}
                  >
                    How We Use Information
                  </a>
                  <a 
                    href="#sharing"
                    className={`nav-item ${activeSection === 'sharing' ? 'active' : ''}`}
                    onClick={(e) => {
                      e.preventDefault();
                      scrollToSection('sharing');
                    }}
                  >
                    Information Sharing
                  </a>
                  <a 
                    href="#security"
                    className={`nav-item ${activeSection === 'security' ? 'active' : ''}`}
                    onClick={(e) => {
                      e.preventDefault();
                      scrollToSection('security');
                    }}
                  >
                    Data Security
                  </a>
                  <a 
                    href="#your-rights"
                    className={`nav-item ${activeSection === 'your-rights' ? 'active' : ''}`}
                    onClick={(e) => {
                      e.preventDefault();
                      scrollToSection('your-rights');
                    }}
                  >
                    Your Rights
                  </a>
                  <a 
                    href="#cookies"
                    className={`nav-item ${activeSection === 'cookies' ? 'active' : ''}`}
                    onClick={(e) => {
                      e.preventDefault();
                      scrollToSection('cookies');
                    }}
                  >
                    Cookies & Tracking
                  </a>
                  <a 
                    href="#children"
                    className={`nav-item ${activeSection === 'children' ? 'active' : ''}`}
                    onClick={(e) => {
                      e.preventDefault();
                      scrollToSection('children');
                    }}
                  >
                    Children's Privacy
                  </a>
                  <a 
                    href="#international"
                    className={`nav-item ${activeSection === 'international' ? 'active' : ''}`}
                    onClick={(e) => {
                      e.preventDefault();
                      scrollToSection('international');
                    }}
                  >
                    International Users
                  </a>
                  <a 
                    href="#changes"
                    className={`nav-item ${activeSection === 'changes' ? 'active' : ''}`}
                    onClick={(e) => {
                      e.preventDefault();
                      scrollToSection('changes');
                    }}
                  >
                    Policy Changes
                  </a>
                  <a 
                    href="#contact"
                    className={`nav-item ${activeSection === 'contact' ? 'active' : ''}`}
                    onClick={(e) => {
                      e.preventDefault();
                      scrollToSection('contact');
                    }}
                  >
                    Contact Us
                  </a>
                </nav>
              </div>
            </aside>

            {/* Main Content */}
            <main className="privacy-main">
              <section id="overview" className="privacy-section">
                <h2>Overview</h2>
                <p>
                  This Privacy Policy describes how EquityWorkx ("we," "us," or "our") collects, uses, 
                  and shares information when you use our services, including our website at 
                  https://www.equityworkx.com, our mobile applications, and our Agent Portal that connects 
                  users with qualified financial advisors and mortgage professionals.
                </p>
                <p>
                  We are committed to protecting your privacy and ensuring you have a positive experience 
                  on our platform. This policy outlines our practices concerning the collection, use, and 
                  disclosure of your information, including how we facilitate connections between users and 
                  financial professionals in our network.
                </p>
                
                <div className="highlight-box">
                  <h3>Key Points</h3>
                  <ul>
                    <li>We collect information you provide directly and automatically through your use of our services</li>
                    <li>We connect you with qualified financial advisors and mortgage agents based on your needs and interests</li>
                    <li>We share limited information with agents to facilitate meaningful professional connections</li>
                    <li>We use bank-level encryption to protect your financial data</li>
                    <li>We never sell your personal information to third parties for their marketing purposes</li>
                    <li>You have control over your data, agent connections, and can request deletion at any time</li>
                    <li>We comply with GDPR, CCPA, and other privacy regulations</li>
                  </ul>
                </div>
              </section>

              <section id="information-we-collect" className="privacy-section">
                <h2>Information We Collect</h2>
                
                <h3>Information You Provide</h3>
                <p>We collect information that you provide directly to us, including:</p>
                <ul>
                  <li><strong>Account Information:</strong> Name, email address, phone number, and password</li>
                  <li><strong>Financial Information:</strong> Mortgage details, bank account information (via secure third-party providers)</li>
                  <li><strong>Profile Information:</strong> Demographic information, financial goals, and preferences</li>
                  <li><strong>Communications:</strong> Feedback, support requests, and other communications with us</li>
                </ul>

                <h3>Information Collected Automatically</h3>
                <p>When you use our services, we automatically collect certain information:</p>
                <ul>
                  <li><strong>Usage Data:</strong> Features used, actions taken, and time spent on our platform</li>
                  <li><strong>Device Information:</strong> Device type, operating system, unique device identifiers</li>
                  <li><strong>Location Data:</strong> General location based on IP address (not precise location)</li>
                  <li><strong>Log Data:</strong> IP address, browser type, pages visited, and referring URLs</li>
                </ul>

                <h3>Information from Third Parties</h3>
                <p>We may receive information about you from third parties:</p>
                <ul>
                  <li><strong>Financial Institutions:</strong> Account balances and transaction history (with your consent)</li>
                  <li><strong>Identity Verification Services:</strong> To verify your identity and prevent fraud</li>
                  <li><strong>Analytics Providers:</strong> To understand how users interact with our services</li>
                </ul>
              </section>

              <section id="how-we-use" className="privacy-section">
                <h2>How We Use Your Information</h2>
                
                <p>We use the information we collect for the following purposes:</p>
                
                <div className="usage-grid">
                  <div className="usage-card">
                    <div className="usage-icon">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                        <circle cx="12" cy="7" r="4"/>
                      </svg>
                    </div>
                    <h4>Provide Services</h4>
                    <p>To deliver our financial planning and wealth-building tools</p>
                  </div>
                  
                  <div className="usage-card">
                    <div className="usage-icon">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/>
                        <circle cx="8.5" cy="7" r="4"/>
                        <circle cx="20" cy="7" r="4"/>
                      </svg>
                    </div>
                    <h4>Connect You with Agents</h4>
                    <p>To match you with qualified financial advisors and mortgage professionals based on your needs and activity</p>
                  </div>
                  
                  <div className="usage-card">
                    <div className="usage-icon">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
                      </svg>
                    </div>
                    <h4>Security & Fraud Prevention</h4>
                    <p>To protect your account and prevent unauthorized access</p>
                  </div>
                  
                  <div className="usage-card">
                    <div className="usage-icon">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/>
                      </svg>
                    </div>
                    <h4>Personalization</h4>
                    <p>To customize your experience and provide relevant insights</p>
                  </div>
                  
                  <div className="usage-card">
                    <div className="usage-icon">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72 12.84 12.84 0 00.7 2.81 2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45 12.84 12.84 0 002.81.7A2 2 0 0122 16.92z"/>
                      </svg>
                    </div>
                    <h4>Lead Generation</h4>
                    <p>To identify when you may benefit from professional guidance and facilitate introductions to appropriate agents</p>
                  </div>
                  
                  <div className="usage-card">
                    <div className="usage-icon">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M3 12h18m-9-9v18"/>
                      </svg>
                    </div>
                    <h4>Improvements</h4>
                    <p>To enhance our services and develop new features</p>
                  </div>
                </div>
                
                <h3>How We Generate Leads for Agents</h3>
                <p>
                  Our platform analyzes your interactions, financial goals, and expressed interests to identify 
                  opportunities where professional guidance could be beneficial. When we detect that you may benefit 
                  from speaking with a financial advisor or mortgage professional, we:
                </p>
                <ul>
                  <li>Evaluate your profile against our network of qualified agents</li>
                  <li>Match you with agents who specialize in your areas of need</li>
                  <li>Share relevant information about your interests and goals with matched agents</li>
                  <li>Facilitate introductions between you and selected professionals</li>
                </ul>
                <p>
                  <strong>Important:</strong> Agent matching is an optional feature. You can opt-out of all agent 
                  matching through your account settings at any time. When opted out, your information will not be 
                  shared with agents for lead generation purposes.
                </p>
              </section>

              <section id="sharing" className="privacy-section">
                <h2>Information Sharing</h2>
                
                <p>
                  We share your information only in specific circumstances to provide our services and connect you 
                  with qualified professionals. We do not sell your personal information to third parties for their 
                  own marketing purposes.
                </p>
                
                <h3>Financial Advisors and Mortgage Agents</h3>
                <p>
                  A core feature of our platform is connecting users with qualified financial professionals. When you 
                  use our services, we may share certain information with agents in our network:
                </p>
                <ul>
                  <li><strong>Lead Information:</strong> Your name, contact information, and general location</li>
                  <li><strong>Financial Interests:</strong> Your expressed financial goals, mortgage interests, and areas where you seek guidance</li>
                  <li><strong>Engagement Data:</strong> Information about your activity on our platform that indicates readiness for professional assistance</li>
                  <li><strong>Profile Summary:</strong> Basic demographic information and financial readiness indicators</li>
                </ul>
                <p>
                  <strong>What We Don't Share with Agents:</strong> We do not share your specific account balances, 
                  transaction history, social security number, or other sensitive financial details unless you explicitly 
                  authorize such sharing for a specific purpose.
                </p>
                
                <div className="highlight-box">
                  <h4>Your Control Over Agent Connections</h4>
                  <p>
                    You have the ability to opt-out of agent matching entirely through your account settings. 
                    This is an all-or-nothing setting - when enabled, agents in our network may receive your 
                    information as described above. When disabled, your information will not be shared with 
                    agents for lead generation purposes.
                  </p>
                </div>
                
                <h3>Service Providers</h3>
                <p>
                  We work with trusted third-party service providers who assist us in operating our platform, 
                  such as cloud storage providers, analytics services, and customer support tools. These 
                  providers are contractually obligated to protect your information and can only use it to provide 
                  services to us.
                </p>
                
                <h3>Legal Requirements</h3>
                <p>
                  We may disclose your information if required by law, court order, or government regulation, 
                  or if we believe disclosure is necessary to protect our rights, your safety, or the safety of others.
                </p>
                
                <h3>Business Transfers</h3>
                <p>
                  In the event of a merger, acquisition, or sale of assets, your information may be transferred 
                  to the acquiring entity. We will notify you of any such change and how it affects your privacy rights.
                </p>
                
                <h3>With Your Consent</h3>
                <p>
                  We may share additional information with your explicit consent or at your direction, such as when 
                  you connect third-party financial accounts or request a specific introduction to an advisor.
                </p>
                
                <h3>Aggregated and Anonymous Data</h3>
                <p>
                  We may share aggregated, anonymized data that cannot be used to identify you personally. This helps 
                  us improve our services and provide insights to our agent network about market trends and user needs.
                </p>
              </section>

              <section id="security" className="privacy-section">
                <h2>Data Security</h2>
                
                <p>
                  We implement comprehensive security measures designed to protect your personal information 
                  from unauthorized access, disclosure, alteration, and destruction. Our security program is 
                  built on industry best practices and continuously evolves to address emerging threats.
                </p>
                
                <div className="security-features">
                  <div className="security-item">
                    <div className="security-icon">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                        <path d="M7 11V7a5 5 0 0110 0v4"/>
                      </svg>
                    </div>
                    <div>
                      <h4>Advanced Encryption Standards</h4>
                      <p>We use AES-256 encryption for data at rest and TLS 1.3 for data in transit, 
                      meeting or exceeding banking industry standards</p>
                    </div>
                  </div>
                  
                  <div className="security-item">
                    <div className="security-icon">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
                        <path d="M9 12l2 2 4-4"/>
                      </svg>
                    </div>
                    <div>
                      <h4>Regular Security Assessments</h4>
                      <p>Quarterly penetration testing, annual SOC 2 Type II audits, and continuous 
                      vulnerability scanning ensure our defenses remain robust</p>
                    </div>
                  </div>
                  
                  <div className="security-item">
                    <div className="security-icon">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1"/>
                      </svg>
                    </div>
                    <div>
                      <h4>Multi-Factor Authentication (MFA)</h4>
                      <p>Required for all accounts with support for authenticator apps, SMS codes, 
                      and biometric authentication where available</p>
                    </div>
                  </div>
                  
                  <div className="security-item">
                    <div className="security-icon">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="12" cy="12" r="3"/>
                        <path d="M12 1v6m0 6v6m4.22-13.22l4.24 4.24M1.54 8.54l4.24 4.24m12.68 0l4.24 4.24M1.54 15.46l4.24-4.24"/>
                      </svg>
                    </div>
                    <div>
                      <h4>Real-Time Threat Detection</h4>
                      <p>AI-powered monitoring systems detect and respond to suspicious activities 
                      24/7, with automated incident response protocols</p>
                    </div>
                  </div>
                  
                  <div className="security-item">
                    <div className="security-icon">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M21 16V8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 003 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l7-4A2 2 0 0021 16z"/>
                        <polyline points="3.27 6.96 12 12.01 20.73 6.96"/>
                        <line x1="12" y1="22.08" x2="12" y2="12"/>
                      </svg>
                    </div>
                    <div>
                      <h4>Secure Infrastructure</h4>
                      <p>Data stored in SOC 2 certified data centers with redundant backups, 
                      physical security controls, and disaster recovery protocols</p>
                    </div>
                  </div>
                  
                  <div className="security-item">
                    <div className="security-icon">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M16 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/>
                        <circle cx="8.5" cy="7" r="4"/>
                        <line x1="20" y1="8" x2="20" y2="14"/>
                        <line x1="23" y1="11" x2="17" y2="11"/>
                      </svg>
                    </div>
                    <div>
                      <h4>Access Controls</h4>
                      <p>Strict role-based access controls, principle of least privilege, and 
                      comprehensive audit logging of all data access</p>
                    </div>
                  </div>
                </div>
                
                <h3>Security Compliance & Certifications</h3>
                <p>
                  We maintain compliance with industry standards and regulations including:
                </p>
                <ul>
                  <li><strong>PCI DSS Level 1:</strong> For secure payment processing</li>
                  <li><strong>ISO 27001:</strong> Information security management certification</li>
                  <li><strong>GDPR & CCPA:</strong> Privacy regulation compliance</li>
                  <li><strong>NIST Cybersecurity Framework:</strong> Comprehensive security controls</li>
                </ul>
                
                <h3>Incident Response</h3>
                <p>
                  In the unlikely event of a security incident, we have established procedures to:
                </p>
                <ul>
                  <li>Immediately contain and investigate the incident</li>
                  <li>Assess the impact and determine affected users</li>
                  <li>Notify affected users within 72 hours as required by law</li>
                  <li>Provide guidance on protective measures you can take</li>
                  <li>Implement improvements to prevent future incidents</li>
                </ul>
                
                <div className="highlight-box">
                  <h4>Your Security Responsibilities</h4>
                  <p>You can help protect your account by:</p>
                  <ul>
                    <li>Using a strong, unique password for your EquityWorkx account</li>
                    <li>Enabling multi-factor authentication</li>
                    <li>Keeping your login credentials confidential</li>
                    <li>Promptly reporting any suspicious account activity</li>
                    <li>Keeping your devices and software up to date</li>
                  </ul>
                </div>
                
                <p>
                  <strong>Security Commitment:</strong> We are committed to maintaining the highest standards 
                  of data security. While no system can guarantee absolute security, we continuously invest in 
                  advanced security technologies and practices to protect your information. Our dedicated security 
                  team works around the clock to ensure your data remains safe and secure.
                </p>
              </section>

              <section id="your-rights" className="privacy-section">
                <h2>Your Privacy Rights</h2>
                
                <p>You have the following rights regarding your personal information:</p>
                
                <div className="rights-list">
                  <div className="right-item">
                    <h4>Access Your Information</h4>
                    <p>Request a copy of the personal information we have about you</p>
                  </div>
                  
                  <div className="right-item">
                    <h4>Update Your Information</h4>
                    <p>Correct or update inaccurate or incomplete personal information</p>
                  </div>
                  
                  <div className="right-item">
                    <h4>Delete Your Information</h4>
                    <p>Request deletion of your personal information, subject to legal requirements</p>
                  </div>
                  
                  <div className="right-item">
                    <h4>Data Portability</h4>
                    <p>Receive your information in a structured, machine-readable format</p>
                  </div>
                  
                  <div className="right-item">
                    <h4>Opt-Out</h4>
                    <p>Opt-out of marketing communications and certain data processing</p>
                  </div>
                  
                  <div className="right-item">
                    <h4>Withdraw Consent</h4>
                    <p>Withdraw consent for data processing where consent is the legal basis</p>
                  </div>
                </div>
                
                <p>
                  To exercise any of these rights, please contact us at privacy@equityworkx.com. 
                  We will respond to your request within 30 days.
                </p>
              </section>

              <section id="cookies" className="privacy-section">
                <h2>Cookies & Tracking Technologies</h2>
                
                <p>
                  We use cookies and similar tracking technologies to enhance your experience on our platform. 
                  Here's how we use them:
                </p>
                
                <h3>Types of Cookies We Use</h3>
                <ul>
                  <li><strong>Essential Cookies:</strong> Required for the platform to function properly</li>
                  <li><strong>Analytics Cookies:</strong> Help us understand how users interact with our services</li>
                  <li><strong>Preference Cookies:</strong> Remember your settings and preferences</li>
                  <li><strong>Marketing Cookies:</strong> Used to deliver relevant advertisements (with consent)</li>
                </ul>
                
                <h3>Managing Cookies</h3>
                <p>
                  You can control cookies through your browser settings. However, disabling certain cookies 
                  may limit your ability to use some features of our platform.
                </p>
              </section>

              <section id="children" className="privacy-section">
                <h2>Children's Privacy</h2>
                
                <p>
                  Our services are not intended for children under 18 years of age. We do not knowingly 
                  collect personal information from children under 18. If you are a parent or guardian 
                  and believe your child has provided us with personal information, please contact us 
                  immediately at privacy@equityworkx.com.
                </p>
              </section>

              <section id="international" className="privacy-section">
                <h2>International Data Transfers</h2>
                
                <p>
                  Your information may be transferred to and processed in countries other than your own. 
                  We ensure appropriate safeguards are in place to protect your information in accordance 
                  with this Privacy Policy and applicable laws.
                </p>
                
                <h3>For EU/UK Residents</h3>
                <p>
                  We comply with GDPR requirements for international data transfers, including the use of 
                  Standard Contractual Clauses and other appropriate safeguards.
                </p>
              </section>

              <section id="changes" className="privacy-section">
                <h2>Changes to This Policy</h2>
                
                <p>
                  We may update this Privacy Policy from time to time to reflect changes in our practices 
                  or legal requirements. We will notify you of any material changes by:
                </p>
                <ul>
                  <li>Posting the updated policy on our website</li>
                  <li>Sending you an email notification</li>
                  <li>Displaying a notice in our mobile app</li>
                </ul>
                <p>
                  The "Last Updated" date at the top of this policy indicates when it was last revised.
                </p>
              </section>

              <section id="contact" className="privacy-section">
                <h2>Contact Us</h2>
                
                <p>If you have questions about this Privacy Policy or our privacy practices, please contact us:</p>
                
                <div className="contact-info">
                  <div className="contact-method">
                    <h4>Email</h4>
                    <a href="mailto:privacy@equityworkx.com">privacy@equityworkx.com</a>
                  </div>
                  
                  <div className="contact-method">
                    <h4>Mail</h4>
                    <address>
                      EquityWorkx, Inc.<br />
                      Privacy Department<br />
                      123 Financial District<br />
                      San Francisco, CA 94105
                    </address>
                  </div>
                  
                  <div className="contact-method">
                    <h4>Phone</h4>
                    <a href="tel:1-800-EQUITY">1-800-EQUITY (378-489)</a>
                  </div>
                </div>
                
                <div className="response-time">
                  <p>
                    <strong>Response Time:</strong> We aim to respond to all privacy inquiries within 
                    48 hours and resolve requests within 30 days.
                  </p>
                </div>
              </section>
            </main>
          </div>
        </div>
      </div>

      <ModernFooter />
    </div>
  );
}